import { z } from 'zod';

import { pagedSchema } from './common';
import { folderResponseSchema } from './folderSchemas';

// #region 메타데이터 (FileResponse에 내장 · PUT /files/{id}/metadata)
export const metadataAttributeSchema = z.object({
  key: z.string(),
  value: z.string(),
});

/** 다중 값 필드 — 서버가 null로 주더라도 화면에서는 빈 배열로 다룬다 */
const multiValueSchema = z
  .array(z.string())
  .nullish()
  .transform((v) => v ?? []);

/**
 * FileMetadataResponse — 공통 필드 + 분류별 attributes.
 *
 * 분류·제품·언어는 스키마(GET /metadata/schema)에서 multi=true라 **복수형 키의 배열**로
 * 온다(categories·products·languages · 2026-10-19 swagger 기준). 스키마의 field 이름은
 * 단수형(category·product·language)이므로 폼은 `METADATA_BODY_KEY`로 둘을 잇는다.
 */
export const fileMetadataResponseSchema = z.object({
  categories: multiValueSchema,
  subCategory: z.string().nullish(),
  products: multiValueSchema,
  materialType: z.string().nullish(),
  fileNature: z.string().nullish(),
  languages: multiValueSchema,
  targetRegion: z.string().nullish(),
  version: z.string().nullish(),
  isLatest: z.boolean().nullish(),
  originalLocation: z.string().nullish(),
  description: z.string().nullish(),
  confidential: z.string().nullish(),
  attributes: z.array(metadataAttributeSchema).nullish(),
});
export type FileMetadataResponse = z.infer<typeof fileMetadataResponseSchema>;

/** PUT /files/{id}/metadata · POST /metadata/validate 요청 바디 (FileMetadataRequest) */
export type FileMetadataRequest = {
  categories: string[];
  subCategory: string | null;
  products: string[];
  materialType: string | null;
  fileNature: string | null;
  languages: string[];
  targetRegion: string | null;
  version: string | null;
  isLatest: boolean | null;
  originalLocation: string | null;
  description: string;
  confidential: string | null;
  attributes: z.infer<typeof metadataAttributeSchema>[];
};

/**
 * 스키마 field(단수) → 요청·응답 바디 키. 여기에 없는 필드는 이름이 같다.
 * 바디 키는 API 계약이라 고정이고, 화면의 라벨·필수·선택지는 스키마를 따른다.
 */
export const METADATA_BODY_KEY: Record<string, string> = {
  category: 'categories',
  product: 'products',
  language: 'languages',
};
// #endregion

// #region 파일 응답 (FileResponse) — N2 상세 · E2 폴더 파일 목록 · N14 장바구니 공용
export const fileResponseSchema = z.object({
  id: z.number(),
  originFileName: z.string(),
  uploadFileName: z.string(),
  fileSize: z.number(),
  mimeType: z.string().nullish(),
  extension: z.string().nullish(),
  thumbnailFileName: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  isDeleted: z.boolean(),
  deletedAt: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  downloadCount: z.number(),
  isRepresentative: z.boolean(),
  /** 이 파일이 연관 파일이면 대표 파일 id */
  representativeFileId: z.number().nullable(),
  relativePath: z.string().nullish(),
  /** 대표 파일이 거느린 연관 파일 수 */
  associatedCount: z.number(),
  /** 폴더 파일 목록의 분류 — 상세에서는 빈 배열. 표시는 `fileCategoryTags`로 */
  derivedCategories: z.array(z.string()),
  /**
   * 소속 폴더 목록 (원본 + alias 다중 경로 · R1 반영으로 추가됨).
   * 상세는 원본이 먼저지만 목록은 순서가 보장되지 않고, 원본 표시 필드도 없다.
   */
  folders: z.array(folderResponseSchema),
  /** 상세에서만 채워진다 — 목록(폴더 파일·장바구니)은 null */
  metadata: fileMetadataResponseSchema.nullish(),
});
export type FileResponse = z.infer<typeof fileResponseSchema>;

/** E2 — GET /files/folders/{folderId} (PaginatedListOfFileResponse) */
export const v2FileListPageSchema = pagedSchema(fileResponseSchema);
export type V2FileListPage = z.infer<typeof v2FileListPageSchema>;
// #endregion

// #region 대표-연관 (GET /files/{id}/assets)
export const associationTypeSchema = z.enum(['Dependency', 'Preview']);

/** 연관 파일 1건 (AssociatedFileResponse) — 상세의 연관 트리 표시용 */
export const associatedFileResponseSchema = z.object({
  id: z.number(),
  representativeFileId: z.number(),
  originFileName: z.string(),
  relativePath: z.string().nullish(),
  associationType: associationTypeSchema.nullish(),
  fileSize: z.number(),
  mimeType: z.string().nullish(),
  extension: z.string().nullish(),
  createdAt: z.string(),
});
export const associatedFileListSchema = z.array(associatedFileResponseSchema);
export type AssociatedFileResponse = z.infer<
  typeof associatedFileResponseSchema
>;
// #endregion

/** N4 — PUT /files/{id}/folders (소속 폴더 전체 집합 교체 · 응답은 FolderResponse[]) */
export const putFileFoldersBodySchema = z.object({
  folderIds: z.array(z.number()).min(1),
});
export type PutFileFoldersBody = z.infer<typeof putFileFoldersBodySchema>;

// #region N16 — POST /files/remove-paths (파일 1개를 여러 경로에서 제거)
export const removeFilePathsBodySchema = z.object({
  fileId: z.number(),
  folderIds: z.array(z.number()).min(1),
});
export type RemoveFilePathsBody = z.infer<typeof removeFilePathsBodySchema>;

/** movedToTrash=true면 남은 경로가 0개라 휴지통으로 이동됨 */
export const removeFilePathsResponseSchema = z.object({
  movedToTrash: z.boolean(),
  remainingFolderIds: z.array(z.number()),
});
export type RemoveFilePathsResponse = z.infer<
  typeof removeFilePathsResponseSchema
>;
// #endregion

/** N13 — POST /files/bulk-download 요청 (폴더는 하위 포함으로 서버가 전개) */
export const bulkDownloadRequestBodySchema = z.object({
  folderIds: z.array(z.number()).nullish(),
  fileIds: z.array(z.number()).nullish(),
});
export type BulkDownloadRequestBody = z.infer<
  typeof bulkDownloadRequestBodySchema
>;

// #region 업로드 커밋 — POST /files/bulk/upload-token → SAS 업로드 → 등록
export const uploadTokenRequestSchema = z.object({
  folderId: z.number(),
  originFileNameList: z.array(z.string()).min(1),
});
export type UploadTokenRequest = z.infer<typeof uploadTokenRequestSchema>;

/** sasToken은 blob에 바로 쓸 수 있는 완전한 SAS URL, key는 저장 파일명(uploadFileName) */
export const uploadTokenItemSchema = z.object({
  originFileName: z.string(),
  key: z.string(),
  sasToken: z.string(),
});
export const uploadTokensResponseSchema = z.object({
  expiresOn: z.string(),
  items: z.array(uploadTokenItemSchema),
});
export type UploadTokensResponse = z.infer<typeof uploadTokensResponseSchema>;

export const uploadModeSchema = z.enum(['Single', 'Package']);
export type UploadMode = z.infer<typeof uploadModeSchema>;

/**
 * POST /files/bulk 항목 (relativePath = 연관 파일 상대경로).
 * metadata는 업로드 시점에 함께 입력하는 초기 메타데이터(선택 · 미입력이면 null).
 */
export type BulkCreateFileItem = {
  originFileName: string;
  uploadFileName: string;
  fileSize: number;
  isRepresentative: boolean;
  relativePath: string | null;
  associationType: z.infer<typeof associationTypeSchema> | null;
  metadata?: FileMetadataRequest | null;
};
/**
 * folderIds: 저장 폴더 집합 — **[0]이 원본 저장 폴더**, 나머지는 추가 소속(alias)
 * (규칙 #3 · 2026-08-24 스웨거에서 folderId → folderIds로 확장).
 */
export type BulkCreateFileBody = {
  folderIds: number[];
  uploadMode: UploadMode;
  createFileRequestList: BulkCreateFileItem[];
};

/** POST /files/bulk/with-folder 항목 (folderPath대로 하위 폴더 생성) */
export type BulkCreateFileWithFolderItem = {
  originFileName: string;
  uploadFileName: string;
  folderPath: string;
  fileSize: number;
  isRepresentative: boolean;
  associationType: z.infer<typeof associationTypeSchema> | null;
  metadata?: FileMetadataRequest | null;
};
export type BulkCreateFileWithFolderBody = {
  /** [0]이 원본 저장 폴더 (BulkCreateFileBody와 동일) */
  folderIds: number[];
  uploadMode: UploadMode;
  createFileRequestList: BulkCreateFileWithFolderItem[];
};
// #endregion

// #region E6 — GET /files/deleted (DeletedFileResponse)
export const deletedFileResponseSchema = z.object({
  id: z.number(),
  originFileName: z.string(),
  uploadFileName: z.string(),
  fileSize: z.number(),
  mimeType: z.string().nullish(),
  extension: z.string().nullish(),
  thumbnailFileName: z.string().nullish(),
  thumbnailUrl: z.string().nullish(),
  isDeleted: z.boolean(),
  deletedAt: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string(),
  downloadCount: z.number(),
  koreanFullPath: z.string(),
  englishFullPath: z.string(),
  /** 대표(패키지) 파일이면 "연관 N개 포함" 표시용 */
  associatedCount: z.number(),
  /**
   * 항목별 처리 가능 여부(DUMP-04 백엔드 요청) — 원래 폴더의 관리 권한 기준.
   * 아직 내려오지 않으면 undefined → 화면은 선택 가능으로 두고 서버가 최종 판정한다.
   */
  canRestore: z.boolean().nullish(),
  canPurge: z.boolean().nullish(),
});
export const v2DeletedFilePageSchema = pagedSchema(deletedFileResponseSchema);
export type DeletedFileResponse = z.infer<typeof deletedFileResponseSchema>;
export type V2DeletedFilePage = z.infer<typeof v2DeletedFilePageSchema>;
// #endregion
